import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import store from 'store2';
import { schedulingAuthApi } from '@/services/auth';

export interface User {
    id: string;
    username: string;
    name: string;
    role: 'admin' | 'operator' | 'viewer';
    avatar?: string;
}

interface AuthState {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    loading: boolean;

    // Actions
    login: (params: {
        username: string;
        password: string;
        captcha: string;
        key: string;
    }) => Promise<boolean>;
    logout: () => void;
    setUser: (user: User | null) => void;
    setToken: (token: string | null) => void;
    checkAuth: () => boolean;
}

const normalizeRole = (
    role: string | undefined,
    roles: Array<string | { code?: string; name?: string }> | undefined
): User['role'] => {
    const allRoles = [
        role,
        ...(roles || []).map((item) =>
            typeof item === 'string' ? item : item.code || item.name || ''
        ),
    ]
        .filter(Boolean)
        .map((item) => String(item).toLowerCase());

    if (allRoles.some((item) => item.includes('admin'))) {
        return 'admin';
    }
    if (
        allRoles.some(
            (item) => item.includes('operator') || item.includes('dispatch')
        )
    ) {
        return 'operator';
    }
    return 'viewer';
};

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            token: null,
            isAuthenticated: false,
            loading: false,

            login: async ({ username, password, captcha, key }) => {
                set({ loading: true });

                try {
                    const response = await schedulingAuthApi.login({
                        username,
                        password,
                        captcha,
                        key,
                    });

                    // Handle nested response: backend wraps data twice
                    const loginData = (response.data as any)?.data ?? response.data;

                    if (response.code !== 0 || !loginData?.token || !loginData.user) {
                        set({ loading: false });
                        return false;
                    }

                    const token = loginData.token;
                    const user: User = {
                        id: loginData.user.id,
                        username:
                            loginData.user.username ||
                            username,
                        name: loginData.user.name || username,
                        role: normalizeRole(loginData.user.role, loginData.roles),
                        avatar: loginData.user.avatar || loginData.user.avatarUrl,
                    };
                    // Store token using store2 (consistent with protocol-sdk's store.get('token'))
                    store.set('token', token);
                    store.set('user', user);
                    store.set('roles', loginData.roles || []);
                    store.set('permissions', loginData.permissions || []);

                    set({
                        user,
                        token,
                        isAuthenticated: true,
                        loading: false,
                    });
                    return true;
                } catch {
                    set({ loading: false });
                    return false;
                }
            },

            logout: () => {
                store.remove('token');
                store.remove('user');
                store.remove('roles');
                store.remove('permissions');
                store.remove('refreshToken');
                set({
                    user: null,
                    token: null,
                    isAuthenticated: false,
                });
            },

            setUser: (user) => set({ user }),

            setToken: (token) => {
                if (token) {
                    store.set('token', token);
                } else {
                    store.remove('token');
                }
                set({ token, isAuthenticated: !!token });
            },

            checkAuth: () => {
                const token = store.get('token');
                const user = store.get('user') || get().user;
                const isAuth = !!(token && user);
                set({ isAuthenticated: isAuth, token, user });
                return isAuth;
            },
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                user: state.user,
                token: state.token,
                isAuthenticated: state.isAuthenticated,
            }),
        }
    )
);
